import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import "../assets/CSS/Dashboard.css";
import "../assets/CSS/WorkersMaster.css";

const emptyForm = {
  employeeId: "", name: "", workerCode: "", department: "",
  whatsappNumber: "", email: "", status: "Active", password: "",
};
const messageOf = (error, fallback) => error.response?.data?.message || fallback;
const mergeDocumentSelection = (current, incoming) => {
  const merged = [...current];
  const existingKeys = new Set(current.map(file => `${file.name}|${file.size}|${file.lastModified}`));
  incoming.forEach(file => {
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (!existingKeys.has(key) && merged.length < 10) {
      existingKeys.add(key);
      merged.push(file);
    }
  });
  return merged;
};
const MAX_DOCUMENT_SIZE = 2 * 1024 * 1024;

export default function WorkersMaster() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [documentsWorker, setDocumentsWorker] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  const loadData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const [workerResponse, optionResponse] = await Promise.all([
        axiosInstance.get("/api/worker-master"),
        axiosInstance.get("/api/worker-master/options"),
      ]);
      setWorkers(workerResponse.data?.workers || []);
      setCanManage(Boolean(workerResponse.data?.canManage));
      setDepartments(optionResponse.data?.departments || []);
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Unable to Load", text: messageOf(error, "Workers load nahi ho paye."), confirmButtonColor: "#1769aa" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredWorkers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return workers.filter(worker => {
      const matchesSearch = !query || [worker.EmployeeID, worker.Name, worker.WorkerCode, worker.WhatsApp_Number]
        .some(value => String(value || "").toLowerCase().includes(query));
      const matchesDepartment = !departmentFilter || worker.Department === departmentFilter;
      const matchesStatus = !statusFilter
        || (statusFilter === "Blocked" ? Boolean(worker.IsBlocked) : worker.Status === statusFilter);
      return matchesSearch && matchesDepartment && matchesStatus;
    });
  }, [workers, search, departmentFilter, statusFilter]);

  const summary = useMemo(() => workers.reduce((result, worker) => {
    result.total += 1;
    if (String(worker.Status).toLowerCase() === "active") result.active += 1;
    if (worker.IsBlocked) result.blocked += 1;
    return result;
  }, { total: 0, active: 0, blocked: 0 }), [workers]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = worker => {
    setEditingId(worker.EmployeeID);
    setForm({
      employeeId: worker.EmployeeID || "",
      name: worker.Name || "",
      workerCode: worker.WorkerCode || "",
      department: worker.Department || "",
      whatsappNumber: worker.WhatsApp_Number || "",
      email: worker.Email || "",
      status: worker.Status || "Active",
      password: "",
    });
    setShowModal(true);
  };

  const openEditDocuments = () => {
    const worker = workers.find(item => item.EmployeeID === editingId);
    if (!worker) return;
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    openDocuments(worker);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const updateForm = event => setForm(previous => ({ ...previous, [event.target.name]: event.target.value }));

  const saveWorker = async event => {
    event.preventDefault();
    if (!form.employeeId.trim() || !form.name.trim() || !form.department) {
      await Swal.fire({ icon: "warning", title: "Required Fields", text: "Employee ID, worker name aur department required hain.", confirmButtonColor: "#1769aa" });
      return;
    }
    if (!editingId && !form.password) {
      await Swal.fire({ icon: "warning", title: "Password Required", text: "New worker ke liye password enter karein.", confirmButtonColor: "#1769aa" });
      return;
    }

    const wasEditing = Boolean(editingId);
    try {
      setSaving(true);
      if (wasEditing) await axiosInstance.put(`/api/worker-master/${encodeURIComponent(editingId)}`, form);
      else await axiosInstance.post("/api/worker-master", form);
      setShowModal(false);
      setEditingId(null);
      setForm(emptyForm);
      await loadData(false);
      await Swal.fire({
        icon: "success", title: wasEditing ? "Worker Updated" : "Worker Added",
        text: wasEditing ? "Worker details successfully update ho gayi." : "New worker successfully add ho gaya.",
        confirmButtonColor: "#1769aa",
      });
    } catch (error) {
      await Swal.fire({
        icon: error.response?.status === 409 ? "warning" : "error",
        title: error.response?.status === 409 ? "Duplicate Record" : "Save Failed",
        text: messageOf(error, "Worker save nahi ho paya."), confirmButtonColor: "#1769aa",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleAccess = async worker => {
    const willBlock = !worker.IsBlocked;
    const result = await Swal.fire({
      icon: "warning",
      title: willBlock ? "Block worker access?" : "Restore worker access?",
      text: willBlock
        ? `${worker.Name} login aur active worker flows use nahi kar payega.`
        : `${worker.Name} ka access restore ho jayega.`,
      showCancelButton: true,
      confirmButtonText: willBlock ? "Yes, block" : "Yes, restore",
      cancelButtonText: "Cancel",
      confirmButtonColor: willBlock ? "#dc3545" : "#198754",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.patch(`/api/worker-master/${encodeURIComponent(worker.EmployeeID)}/access`, { blocked: willBlock });
      await loadData(false);
      await Swal.fire({ icon: "success", title: willBlock ? "Access Blocked" : "Access Restored", timer: 1500, showConfirmButton: false });
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Action Failed", text: messageOf(error, "Access update nahi ho paya."), confirmButtonColor: "#1769aa" });
    }
  };

  const loadDocuments = async (worker, showLoader = true) => {
    try {
      if (showLoader) setDocumentsLoading(true);
      const response = await axiosInstance.get(`/api/worker-master/${encodeURIComponent(worker.EmployeeID)}/documents`);
      setDocuments(response.data?.documents || []);
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Unable to Load", text: messageOf(error, "Worker documents load nahi ho paye."), confirmButtonColor: "#1769aa" });
    } finally {
      setDocumentsLoading(false);
    }
  };

  const openDocuments = async worker => {
    setDocumentsWorker(worker);
    setDocuments([]);
    setSelectedDocuments([]);
    await loadDocuments(worker);
  };

  const closeDocuments = () => {
    if (uploadingDocuments) return;
    setDocumentsWorker(null);
    setDocuments([]);
    setSelectedDocuments([]);
  };

  const selectDocuments = async event => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = "";
    const validFiles = incoming.filter(file => file.size <= MAX_DOCUMENT_SIZE);
    const rejectedFiles = incoming.filter(file => file.size > MAX_DOCUMENT_SIZE);
    setSelectedDocuments(current => mergeDocumentSelection(current, validFiles));
    if (rejectedFiles.length) {
      await Swal.fire({ icon: "warning", title: "File Too Large", text: `${rejectedFiles.map(file => file.name).join(", ")} upload nahi hoga. Har document maximum 2 MB hona chahiye.`, confirmButtonColor: "#1769aa" });
    }
  };

  const uploadDocuments = async () => {
    if (!selectedDocuments.length) {
      await Swal.fire({ icon: "warning", title: "Select Documents", text: "Upload ke liye kam se kam ek document select karein.", confirmButtonColor: "#1769aa" });
      return;
    }
    const confirmation = await Swal.fire({
      icon: "question",
      title: "Upload selected documents?",
      text: `${selectedDocuments.length} document(s) will be uploaded. Do you want to continue?`,
      showCancelButton: true,
      confirmButtonText: "Yes, upload",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1769aa",
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;
    const formData = new FormData();
    selectedDocuments.forEach(file => formData.append("documents", file));
    try {
      setUploadingDocuments(true);
      const response = await axiosInstance.post(
        `/api/worker-master/${encodeURIComponent(documentsWorker.EmployeeID)}/documents`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSelectedDocuments([]);
      await Promise.all([loadDocuments(documentsWorker, false), loadData(false)]);
      await Swal.fire({ icon: "success", title: "Documents Uploaded", text: response.data?.message || "Documents successfully upload ho gaye.", confirmButtonColor: "#1769aa" });
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Upload Failed", text: messageOf(error, "Documents upload nahi ho paye."), confirmButtonColor: "#1769aa" });
    } finally {
      setUploadingDocuments(false);
    }
  };

  const formatFileSize = size => {
    const bytes = Number(size || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const columns = [
    {
      name: "WORKER",
      minWidth: "230px",
      selector: row => row.Name,
      sortable: true,
      cell: row => (
        <div className="wm-worker-cell">
          <div><strong>{row.Name}</strong><span>ID: {row.EmployeeID}</span></div>
        </div>
      ),
    },
    { name: "WORKER CODE", selector: row => row.WorkerCode || "-", sortable: true, width: "140px" },
    { name: "EMPLOYEE ID", selector: row => row.EmployeeID || "-", sortable: true, width: "135px" },
    { name: "DEPARTMENT", selector: row => row.Department || "-", sortable: true, minWidth: "190px" },
    {
      name: "CONTACT", minWidth: "190px",
      cell: row => <div className="wm-contact"><span>{row.WhatsApp_Number || "No phone"}</span><small>{row.Email || "No email"}</small></div>,
    },
    {
      name: "DOCUMENTS", width: "125px",
      cell: row => <button type="button" className={`wm-document-count ${Number(row.DocumentCount) > 0 ? "has-files" : ""}`} onClick={() => openDocuments(row)}><i className="bi bi-paperclip" /> {Number(row.DocumentCount) || 0}</button>,
    },
    {
      name: "STATUS", width: "130px",
      cell: row => <span className={`wm-status ${row.IsBlocked ? "blocked" : String(row.Status || "").toLowerCase()}`}>{row.IsBlocked ? "Blocked" : row.Status || "-"}</span>,
    },
    {
      name: "ACTIONS", width: canManage ? "165px" : "85px", ignoreRowClick: true, button: true,
      cell: row => (
        <div className="wm-actions">
          <button type="button" className="documents" onClick={() => openDocuments(row)} title="Worker documents"><i className="bi bi-folder2-open" /></button>
          {canManage && <button type="button" onClick={() => openEdit(row)} title="Edit worker"><i className="bi bi-pencil-square" /></button>}
          {canManage && <button type="button" className={row.IsBlocked ? "restore" : "block"} onClick={() => toggleAccess(row)} title={row.IsBlocked ? "Restore access" : "Block access"}>
            <i className={`bi ${row.IsBlocked ? "bi-unlock" : "bi-lock"}`} />
          </button>}
        </div>
      ),
    },
  ];

  return (
    <div className="d-flex dashboard workers-master-page">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}><Sidebar /></div>
      <div className="flex-grow-1">
        <Header toggleSidebar={() => setIsSidebarVisible(value => !value)} isSidebarVisible={isSidebarVisible} />
        <main className="main-container p-4 wm-content">
          <section className="wm-hero">
            <div><span>EMPLOYEE DIRECTORY</span><h2>Workers Master</h2><p>Worker profiles, departments aur account access manage karein.</p></div>
            {canManage && <button className="btn wm-add" onClick={openAdd}><i className="bi bi-person-plus" /> Add Worker</button>}
          </section>

          <section className="wm-summary">
            <div><i className="bi bi-people" /><span><small>Total Workers</small><strong>{summary.total}</strong></span></div>
            <div><i className="bi bi-person-check" /><span><small>Active</small><strong>{summary.active}</strong></span></div>
            <div><i className="bi bi-person-lock" /><span><small>Blocked</small><strong>{summary.blocked}</strong></span></div>
          </section>

          <section className="wm-table-card">
            <header>
              <div><h5>Worker Directory</h5><p>{filteredWorkers.length} records shown</p></div>
              {!canManage && <span className="wm-readonly"><i className="bi bi-eye" /> Read-only access</span>}
            </header>
            <div className="wm-filters">
              <div className="wm-search"><i className="bi bi-search" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search name, ID, worker code or phone..." /></div>
              <select value={departmentFilter} onChange={event => setDepartmentFilter(event.target.value)}>
                <option value="">All Departments</option>{departments.map(department => <option key={department}>{department}</option>)}
              </select>
              <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                <option value="">All Status</option><option>Active</option><option>Inactive</option><option>Resigned</option><option>Blocked</option>
              </select>
            </div>
            <DataTable
              columns={columns} data={filteredWorkers} progressPending={loading} pagination paginationPerPage={15}
              paginationRowsPerPageOptions={[15, 25, 50, 100]} highlightOnHover persistTableHead responsive
              progressComponent={<div className="p-5"><div className="spinner-border text-primary" /></div>}
              noDataComponent={<div className="p-5 text-muted">Koi worker record nahi mila.</div>}
            />
          </section>
        </main>
      </div>

      {showModal && (
        <><div className="modal fade show d-block" tabIndex="-1" aria-modal="true" role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <form className="modal-content wm-modal" onSubmit={saveWorker}>
              <div className="modal-header"><div><h5>{editingId ? "Edit Worker" : "Add Worker"}</h5><small>{editingId ? `Employee ID: ${editingId}` : "Create a new worker profile"}</small></div><button type="button" className="btn-close btn-close-white" onClick={closeModal} disabled={saving} /></div>
              <div className="modal-body"><div className="row g-3">
                <div className="col-md-6"><label>Employee ID *</label><input className="form-control" name="employeeId" value={form.employeeId} onChange={updateForm} required disabled={Boolean(editingId)} /></div>
                <div className="col-md-6"><label>Worker Name *</label><input className="form-control" name="name" value={form.name} onChange={updateForm} maxLength={100} required /></div>
                <div className="col-md-6"><label>Worker Code</label><input className="form-control" name="workerCode" value={form.workerCode} onChange={updateForm} maxLength={50} /></div>
                <div className="col-md-6"><label>Department *</label><select className="form-select" name="department" value={form.department} onChange={updateForm} required><option value="">Select Department</option>{departments.map(department => <option key={department}>{department}</option>)}</select></div>
                <div className="col-md-6"><label>WhatsApp Number</label><input className="form-control" name="whatsappNumber" value={form.whatsappNumber} onChange={updateForm} maxLength={20} /></div>
                <div className="col-md-6"><label>Email</label><input type="email" className="form-control" name="email" value={form.email} onChange={updateForm} /></div>
                <div className="col-md-6"><label>Status *</label><select className="form-select" name="status" value={form.status} onChange={updateForm}><option>Active</option><option>Inactive</option><option>Resigned</option></select></div>
                <div className="col-md-6"><label>{editingId ? "New Password" : "Password *"}</label><input type="password" className="form-control" name="password" value={form.password} onChange={updateForm} required={!editingId} placeholder={editingId ? "Leave blank to keep unchanged" : "Enter worker password"} /></div>
                {editingId && <div className="col-12"><label>Uploaded Documents</label><div className="wm-edit-documents"><span><i className="bi bi-folder-check" /><strong>{Number(workers.find(item => item.EmployeeID === editingId)?.DocumentCount) || 0}</strong> document(s) uploaded</span><button type="button" className="btn btn-outline-primary btn-sm" onClick={openEditDocuments}><i className="bi bi-folder2-open me-2" />View / Upload Documents</button></div></div>}
              </div></div>
              <div className="modal-footer"><button type="button" className="btn btn-light" onClick={closeModal} disabled={saving}>Cancel</button><button className="btn btn-primary" disabled={saving}>{saving && <span className="spinner-border spinner-border-sm me-2" />}{editingId ? "Save Changes" : "Add Worker"}</button></div>
            </form>
          </div>
        </div><div className="modal-backdrop fade show" /></>
      )}

      {documentsWorker && (
        <><div className="modal fade show d-block" tabIndex="-1" aria-modal="true" role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content wm-modal wm-documents-modal">
              <div className="modal-header">
                <div><h5><i className="bi bi-folder2-open me-2" />Worker Documents</h5><small>{documentsWorker.Name} · ID {documentsWorker.EmployeeID}</small></div>
                <button type="button" className="btn-close btn-close-white" onClick={closeDocuments} disabled={uploadingDocuments} />
              </div>
              <div className="modal-body">
                {canManage && (
                  <section className="wm-upload-panel">
                    <div className="wm-upload-title"><i className="bi bi-cloud-arrow-up" /><div><strong>Upload documents</strong><span>PDF, image, Word or Excel · maximum 10 files · 2 MB each</span></div></div>
                    <input
                      type="file" multiple className="form-control"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                      onChange={selectDocuments}
                      disabled={uploadingDocuments}
                    />
                    {selectedDocuments.length > 0 && (
                      <div className="wm-selected-files">
                        {selectedDocuments.map((file, index) => <span key={`${file.name}-${file.size}-${file.lastModified}`}><i className="bi bi-file-earmark" /><strong title={file.name}>{file.name}</strong><small>{formatFileSize(file.size)}</small><button type="button" className="wm-selected-remove" onClick={() => setSelectedDocuments(current => current.filter((_, fileIndex) => fileIndex !== index))} title="Remove selected file" aria-label={`Remove ${file.name}`}><i className="bi bi-x-circle-fill" /></button></span>)}
                      </div>
                    )}
                    <button className="btn btn-primary" onClick={uploadDocuments} disabled={uploadingDocuments || !selectedDocuments.length}>
                      {uploadingDocuments ? <><span className="spinner-border spinner-border-sm me-2" />Uploading...</> : <><i className="bi bi-cloud-arrow-up me-2" />Upload {selectedDocuments.length || ""} Document{selectedDocuments.length === 1 ? "" : "s"}</>}
                    </button>
                  </section>
                )}

                <section className="wm-document-list-section">
                  <div className="wm-document-heading"><strong>Uploaded documents</strong><span>{documents.length} file{documents.length === 1 ? "" : "s"}</span></div>
                  {documentsLoading ? (
                    <div className="wm-doc-state"><div className="spinner-border text-primary" /><span>Documents load ho rahe hain...</span></div>
                  ) : documents.length === 0 ? (
                    <div className="wm-doc-state"><i className="bi bi-folder-x" /><strong>No documents uploaded</strong><span>Is worker ke liye abhi koi document nahi hai.</span></div>
                  ) : (
                    <div className="wm-document-list">
                      {documents.map(document => (
                        <a href={document.webUrl} target="_blank" rel="noreferrer" className="wm-document-item" key={document.id}>
                          <i className="bi bi-file-earmark-text" />
                          <div><strong>{document.originalName || document.name.replace(/^\d+_\d+_/, "")}</strong><span>{formatFileSize(document.size)} · {new Date(document.lastModifiedDateTime).toLocaleString()}</span></div>
                          <i className="bi bi-box-arrow-up-right" />
                        </a>
                      ))}
                    </div>
                  )}
                </section>
              </div>
              <div className="modal-footer"><button className="btn btn-light" onClick={closeDocuments} disabled={uploadingDocuments}>Close</button></div>
            </div>
          </div>
        </div><div className="modal-backdrop fade show" /></>
      )}
    </div>
  );
}
